/**
 * TanStack Query 没有实体归一化，同一帖子会出现在多个列表缓存里。
 * 这里把「按 id 更新所有缓存中的 Post / User」封装掉，
 * 供点赞 / 转发 / 关注的乐观更新与成功回填共用。
 * 见 design/04-frontend.md「乐观更新」。
 */

import type { InfiniteData, QueryClient } from '@tanstack/react-query'

import type { Page, Post, User } from '@/api/types'
import { isDeleted } from '@/api/types'

type PostUpdater = (post: Post) => Post
type PostMatcher = (post: Post) => boolean

/** 对一个 Post 及其嵌套的 repost_of / quote_of 逐层套用更新 */
function applyToPostTree(post: Post, match: PostMatcher, updater: PostUpdater): Post {
  let next = post

  if (match(post)) {
    next = updater(next)
  }

  const repostOf = next.repost_of
  if (repostOf && !isDeleted(repostOf)) {
    const updated = applyToPostTree(repostOf, match, updater)
    if (updated !== repostOf) next = { ...next, repost_of: updated }
  }

  const quoteOf = next.quote_of
  if (quoteOf && !isDeleted(quoteOf)) {
    const updated = applyToPostTree(quoteOf, match, updater)
    if (updated !== quoteOf) next = { ...next, quote_of: updated }
  }

  return next
}

/** 用户页「帖子」tab 的页面多一个 pinned（D69），它也要跟着更新 */
type PostPage = Page<Post> & { pinned?: Post | null }
type MaybePage = InfiniteData<PostPage> | PostPage | Post | { post: Post } | undefined

function patchContainer(data: MaybePage, match: PostMatcher, updater: PostUpdater): MaybePage {
  if (!data) return data
  const apply = (post: Post) => applyToPostTree(post, match, updater)

  // useInfiniteQuery 的数据
  if ('pages' in data && Array.isArray(data.pages)) {
    return {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        items: page.items.map(apply),
        ...(page.pinned ? { pinned: apply(page.pinned) } : {}),
      })),
    }
  }

  // 单页列表
  if ('items' in data && Array.isArray(data.items)) {
    return { ...data, items: data.items.map(apply) }
  }

  // { ancestors, post } 这类结构
  if ('post' in data && data.post) {
    const container = data as { post: Post; ancestors?: Post[] }
    return {
      ...container,
      post: apply(container.post),
      ...(container.ancestors ? { ancestors: container.ancestors.map(apply) } : {}),
    } as MaybePage
  }

  // 裸 Post
  if ('id' in data && 'counts' in data) {
    return apply(data as Post)
  }

  return data
}

/** 在所有 timeline / post / search 缓存里，更新满足条件的每一条帖子 */
export function patchPostsWhere(client: QueryClient, match: PostMatcher, updater: PostUpdater) {
  for (const prefix of [['timeline'], ['post'], ['search']]) {
    client.setQueriesData({ queryKey: prefix }, (data: MaybePage) =>
      patchContainer(data, match, updater),
    )
  }
}

/** 在所有 timeline / post / user 相关缓存里更新这条帖子 */
export function patchPostEverywhere(client: QueryClient, id: string, updater: PostUpdater) {
  patchPostsWhere(client, (post) => post.id === id, updater)
}

/** 用服务端返回的对象覆盖乐观值（counts 与 viewer 以服务端为准） */
export function replacePostEverywhere(client: QueryClient, fresh: Post) {
  patchPostEverywhere(client, fresh.id, (post) => ({
    ...post,
    counts: fresh.counts,
    viewer: fresh.viewer ?? post.viewer,
  }))
}

type UserUpdater = (user: User) => User

/** 在所有 user 缓存里更新这个用户（关注按钮的乐观更新） */
export function patchUserEverywhere(client: QueryClient, username: string, updater: UserUpdater) {
  const matches = (user: User) => user.username.toLowerCase() === username.toLowerCase()

  client.setQueriesData({ queryKey: ['user'] }, (data: unknown) => {
    if (!data) return data
    const record = data as Record<string, unknown>

    if ('username' in record) {
      const user = data as User
      return matches(user) ? updater(user) : user
    }
    if ('pages' in record && Array.isArray(record.pages)) {
      return {
        ...record,
        pages: (record.pages as Page<User>[]).map((page) => ({
          ...page,
          items: page.items.map((user) => (matches(user) ? updater(user) : user)),
        })),
      }
    }
    if ('items' in record && Array.isArray(record.items)) {
      return {
        ...record,
        items: (record.items as User[]).map((user) => (matches(user) ? updater(user) : user)),
      }
    }
    return data
  })

  client.setQueriesData({ queryKey: ['users', 'suggestions'] }, (data: unknown) => {
    if (!Array.isArray(data)) return data
    return (data as User[]).map((user) => (matches(user) ? updater(user) : user))
  })
}

/** 新帖插到主页时间线第一页顶部，不重新拉取（避免列表跳动） */
export function prependPostToTimeline(
  client: QueryClient,
  queryKey: readonly unknown[],
  post: Post,
) {
  client.setQueryData(queryKey, (data: InfiniteData<Page<Post>> | undefined) => {
    if (!data || data.pages.length === 0) return data
    const [first, ...rest] = data.pages
    if (first.items.some((item) => item.id === post.id)) return data
    return { ...data, pages: [{ ...first, items: [post, ...first.items] }, ...rest] }
  })
}

/** 删除后从所有列表里摘掉（含针对它的转发行） */
export function removePostEverywhere(client: QueryClient, id: string) {
  const drop = (post: Post) => {
    if (post.id === id) return false
    const target = post.repost_of
    if (target && !isDeleted(target) && target.id === id) return false
    return true
  }

  client.setQueriesData({ queryKey: ['timeline'] }, (data: unknown) => {
    if (!data) return data
    const record = data as Record<string, unknown>
    if ('pages' in record && Array.isArray(record.pages)) {
      return {
        ...record,
        pages: (record.pages as PostPage[]).map((page) => ({
          ...page,
          items: page.items.filter(drop),
          ...(page.pinned && !drop(page.pinned) ? { pinned: null } : {}),
        })),
      }
    }
    return data
  })
}
